/*
 * Copyright (C) 2022 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Inject, Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DataFormsDialogComponent } from '../../components/data-forms/data-forms-dialog/data-forms-dialog.component';
import { LanguageCodeHelper } from '../../helpers/language-code-helper';
import { AnswerRequest, DataForm, DataFormSummary, DataFormWithAnswers, UserDataForm } from '../../models/data-form/data-form.model';
import { DATA_FORM_DATA_SERVICE, DataFormDataService } from './data-form-data.service';

/** What the data form dialog closes with once the learner submits. */
interface SubmittedForm {
  answers: AnswerRequest[];
  pageLocation: Location;
  userFormUid: string;
}

@Injectable({
  providedIn: 'root',
})
export class DataFormDialogService {
  public dataForms: DataFormSummary[] = [];
  private canClose: boolean;
  private retake: boolean;
  private onClose: (hasAnswers: boolean) => void;

  constructor(
    public dialog: MatDialog,
    private translocoService: TranslocoService,
    @Inject(DATA_FORM_DATA_SERVICE) private dataFormDataService: DataFormDataService,
  ) {}

  displayDialog(dataForms: DataFormSummary[], canClose: boolean, onClose: () => void) {
    this.canClose = canClose;
    this.retake = false;
    this.onClose = onClose;
    this.getFormData(dataForms);
  }

  displayDialogForRetake(dataForm: DataForm, userForm: UserDataForm, onClose: (hasAnswers: boolean) => void) {
    this.canClose = true;
    this.retake = true;
    this.onClose = onClose;
    this.openDialog([{ dataForm: dataForm, userForm: userForm }]);
  }

  /**
   * Opens a form the learner fills in before going on, such as a registration form. The answers are saved without
   * reloading the page, and the learner can close it to go back: like a retake, it shows their earlier answers, has no
   * skip and closing it stays on the page.
   *
   * @return whether the form was submitted and its answers saved
   */
  completeForm(dataFormUid: string): Observable<boolean> {
    return this.dataFormDataService.getDataForm(dataFormUid).pipe(
      switchMap((dataForm) =>
        dataForm.isSuccess
          ? this.dataFormDataService
              .createUserForm(dataForm.value.uid)
              .pipe(map((userForm) => (userForm.isSuccess ? { dataForm: dataForm.value, userForm: userForm.value } : null)))
          : of(null),
      ),
      switchMap((response) => (response ? this.open([response], true, true).afterClosed() : of<SubmittedForm[] | undefined>(undefined))),
      switchMap((form: SubmittedForm[] | undefined) =>
        form?.length
          ? forkJoin(form.map(({ userFormUid, answers }) => this.dataFormDataService.updateUserForm(userFormUid, { answers }))).pipe(
              map((results) => results.every((result) => result.isSuccess)),
            )
          : of(false),
      ),
    );
  }

  getFormData(summaryArr: DataFormSummary[]) {
    const responses: DataFormWithAnswers[] = [];
    const summaryCount = summaryArr.length;
    let currentCount = 0;

    for (const summary of summaryArr) {
      this.dataFormDataService.getDataForm(summary.dataFormUid).subscribe((response) => {
        if (response.isSuccess) {
          if (summary.userData) {
            responses.push({ dataForm: response.value, userForm: summary.userData });
            currentCount++;
            this.checkResponsesAndOpenDialog(responses, currentCount, summaryCount);
          } else {
            this.dataFormDataService.createUserForm(response.value.uid).subscribe((userForm) => {
              if (userForm.isSuccess) {
                responses.push({ dataForm: response.value, userForm: userForm.value });
                currentCount++;
                this.checkResponsesAndOpenDialog(responses, currentCount, summaryCount);
              }
            });
          }
        }
      });
    }
  }

  sendFormData(form: SubmittedForm[]) {
    const formCount = form.length;
    let count = 0;

    for (const { userFormUid, answers, pageLocation } of form) {
      this.dataFormDataService.updateUserForm(userFormUid, { answers }).subscribe((response) => {
        if (response.isSuccess) {
          count++;
          if (!this.retake && formCount === count) {
            pageLocation.reload();
          }
        }
      });
    }
  }

  openDialog(response: DataFormWithAnswers[]) {
    let dialogRef = this.open(response, this.canClose, this.retake);

    dialogRef.afterClosed().subscribe((form: SubmittedForm[]) => {
      this.onClose(!!form?.length);
      dialogRef = null;
      if (form?.length) {
        this.sendFormData(form);
      }
    });
  }

  private open(response: DataFormWithAnswers[], closable: boolean, retake: boolean): MatDialogRef<DataFormsDialogComponent> {
    if (this.dialog) {
      this.dialog.closeAll();
    }
    let canClose = false;

    for (const { dataForm } of response) {
      canClose = closable || !dataForm.mandatory || canClose;
    }

    return this.dialog.open(DataFormsDialogComponent, {
      width: '90vw',
      maxWidth: '41.875rem',
      maxHeight: '80vh',
      position: {
        top: '10vh',
        bottom: '10vh',
      },
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: ['page-publish-dialog', 'ptl-mat-dialog'],
      backdropClass: 'dialog-backdrop',
      ariaLabel: this.translocoService.translate('translations.resubmitFormDialog'),
      disableClose: !canClose,
      autoFocus: false,
      data: {
        canClose: canClose,
        retake,
        response,
      },
    });
  }

  private checkResponsesAndOpenDialog(responses: DataFormWithAnswers[], currentCount: number, summaryCount: number) {
    if (responses.length && currentCount === summaryCount) {
      this.openDialog(responses);
    }
  }
}
